import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Slide from "@mui/material/Slide";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import { keyframes } from "@emotion/react";

const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255, 144, 21, 0.12)";
const ORANGE_GLOW = "rgba(255, 144, 21, 0.35)";
const ORANGE_DARK = "#e07400";

/* ── Keyframe animations ─────────────────────────────────────────── */

const iconPulse = keyframes`
    0%, 100% { transform: scale(1); opacity: 1; }
    50%      { transform: scale(1.12); opacity: 0.85; }
`;

const iconEntrance = keyframes`
    0%   { transform: scale(0.3) rotate(-15deg); opacity: 0; }
    60%  { transform: scale(1.1) rotate(3deg); opacity: 1; }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const fadeSlideUp = keyframes`
    0%   { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * MobileConfirmDialog - Reusable MUI confirm dialog for mobile.
 * Renders above the full-screen Dialog (zIndex 1500).
 *
 * @param {boolean} open - Whether the dialog is visible
 * @param {string|ReactNode} header - Dialog title
 * @param {string|ReactNode} message - Confirmation message
 * @param {string|ReactNode} acceptLabel - Accept button label
 * @param {string|ReactNode} rejectLabel - Reject button label (optional)
 * @param {function} onAccept - Called when accept is clicked
 * @param {function} onReject - Called when reject is clicked (optional)
 * @param {function} onClose - Called when dialog is dismissed
 */
export default function MobileConfirmDialog({
    open,
    header = "Confirmation",
    message,
    acceptLabel = "OK",
    rejectLabel,
    onAccept,
    onReject,
    onClose,
}) {
    const handleAccept = () => {
        if (onAccept) onAccept();
        if (onClose) onClose();
    };

    const handleReject = () => {
        if (onReject) onReject();
        if (onClose) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            transitionDuration={{ enter: 400, exit: 280 }}
            sx={{
                zIndex: 1500,
                "& .MuiBackdrop-root": {
                    backdropFilter: "blur(8px) saturate(1.4)",
                    WebkitBackdropFilter: "blur(8px) saturate(1.4)",
                    backgroundColor: "rgba(0, 0, 0, 0.35)",
                    transition: "all 400ms cubic-bezier(0.4, 0, 0.2, 1)",
                },
            }}
            PaperProps={{
                elevation: 0,
                sx: {
                    borderRadius: "20px",
                    mx: 2.5,
                    maxWidth: "sm",
                    width: "100%",
                    overflow: "hidden",
                    background:
                        "linear-gradient(168deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.92) 100%)",
                    backdropFilter: "blur(20px) saturate(1.8)",
                    WebkitBackdropFilter: "blur(20px) saturate(1.8)",
                    border: "1px solid rgba(255, 144, 21, 0.12)",
                    boxShadow: [
                        "0 2px 8px rgba(255, 144, 21, 0.06)",
                        "0 8px 24px rgba(255, 144, 21, 0.10)",
                        "0 24px 48px rgba(0, 0, 0, 0.08)",
                    ].join(", "),
                    position: "relative",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background: `linear-gradient(90deg, ${ORANGE}, #FFC100, ${ORANGE})`,
                        backgroundSize: "200% auto",
                        animation: `${shimmer} 3s ease-in-out infinite`,
                        borderRadius: "20px 20px 0 0",
                    },
                },
            }}
        >
            {/* ── Title ────────────────────────────────────────────── */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    pt: 3,
                    pb: 0.5,
                    px: 3,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    letterSpacing: "-0.02em",
                    color: "rgba(0, 0, 0, 0.87)",
                    animation: `${fadeSlideUp} 450ms cubic-bezier(0, 0, 0.2, 1) 100ms both`,
                }}
            >
                {/* Warning icon with glow halo */}
                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 42,
                        height: 42,
                        minWidth: 42,
                        borderRadius: "12px",
                        background: ORANGE_LIGHT,
                        animation: `${iconEntrance} 500ms cubic-bezier(0.34, 1.56, 0.64, 1) 150ms both`,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: -3,
                            borderRadius: "15px",
                            background: `radial-gradient(circle, ${ORANGE_GLOW} 0%, transparent 70%)`,
                            opacity: 0.4,
                            animation: `${iconPulse} 2.5s ease-in-out infinite`,
                            pointerEvents: "none",
                        },
                    }}
                >
                    <WarningAmberIcon
                        sx={{
                            color: ORANGE,
                            fontSize: 22,
                            filter: `drop-shadow(0 1px 3px ${ORANGE_GLOW})`,
                        }}
                    />
                </Box>
                {header}
            </DialogTitle>

            {/* ── Content ──────────────────────────────────────────── */}
            <DialogContent sx={{ px: 3, pt: 1.5, pb: 1 }}>
                <Typography
                    variant="body1"
                    sx={{
                        color: "rgba(0, 0, 0, 0.55)",
                        lineHeight: 1.7,
                        fontSize: "0.93rem",
                        fontWeight: 400,
                        letterSpacing: "0.005em",
                        animation: `${fadeSlideUp} 450ms cubic-bezier(0, 0, 0.2, 1) 200ms both`,
                    }}
                >
                    {message}
                </Typography>
            </DialogContent>

            {/* ── Actions ──────────────────────────────────────────── */}
            <DialogActions
                sx={{
                    px: 3,
                    pb: 3,
                    pt: 2,
                    gap: 1.25,
                    flexDirection: "column",
                    animation: `${fadeSlideUp} 450ms cubic-bezier(0, 0, 0.2, 1) 300ms both`,
                }}
            >
                {/* ── Accept button ── */}
                <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    onClick={handleAccept}
                    sx={{
                        background: `linear-gradient(135deg, ${ORANGE} 0%, #FFA940 100%)`,
                        color: "#fff",
                        fontWeight: 650,
                        textTransform: "none",
                        borderRadius: "14px",
                        minHeight: 50,
                        fontSize: "0.96rem",
                        letterSpacing: "0.01em",
                        position: "relative",
                        overflow: "hidden",
                        boxShadow: `0 4px 14px rgba(255, 144, 21, 0.30)`,
                        transition: "all 280ms cubic-bezier(0.4, 0, 0.2, 1)",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 60%)",
                            borderRadius: "inherit",
                            pointerEvents: "none",
                        },
                        "&:hover": {
                            background: `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 100%)`,
                            boxShadow: `0 6px 20px rgba(255, 144, 21, 0.40)`,
                            transform: "translateY(-1px)",
                        },
                        "&:active": {
                            transform: "scale(0.98) translateY(0px)",
                            boxShadow: `0 2px 8px rgba(255, 144, 21, 0.25)`,
                            transition: "all 100ms ease",
                        },
                    }}
                >
                    {acceptLabel}
                </Button>

                {/* ── Reject button ── */}
                {rejectLabel && (
                    <Button
                        fullWidth
                        variant="text"
                        onClick={handleReject}
                        sx={{
                            borderColor: "transparent",
                            color: "rgba(0, 0, 0, 0.55)",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: "14px",
                            minHeight: 48,
                            fontSize: "0.93rem",
                            letterSpacing: "0.005em",
                            background: "rgba(0, 0, 0, 0.03)",
                            transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                background: "rgba(0, 0, 0, 0.06)",
                                color: "rgba(0, 0, 0, 0.75)",
                            },
                            "&:active": {
                                transform: "scale(0.98)",
                                transition: "all 100ms ease",
                            },
                        }}
                    >
                        {rejectLabel}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
